import { useCallback, useEffect, useRef, useState } from 'react';
import webSocketService from '../services/webSocketService';
import patientLocationService, { TERMINAL_SHARE_STATUSES } from '../services/patientLocationService';

const POLL_INTERVAL_MS = 10000;

/** The patient's shared coordinates, or null until they have shared one */
export const toPatientLocation = (share) => {
  if (share?.status !== 'CAPTURED' || !share.location) return null;
  const { latitude, longitude, accuracyM, capturedAt } = share.location;
  if (latitude == null || longitude == null) return null;
  return { latitude: Number(latitude), longitude: Number(longitude), accuracyM, capturedAt };
};

/** Short status text for a link, e.g. "Shared - 12 m" */
export const describeLocationShare = (share) => {
  if (!share) return null;
  switch (share.status) {
    case 'CAPTURED': {
      const acc = share.location?.accuracyM;
      return acc != null ? `Shared \u00b7 \u00b1${Math.round(acc)} m` : 'Shared';
    }
    case 'OPENED': return 'Link opened';
    case 'CREATED': return 'Link sent';
    case 'REVOKED': return 'Link cancelled';
    case 'EXPIRED': return 'Link expired';
    default: return null;
  }
};

/** Patient phone with its country code, in the form the backend accepts */
export const bookingPatientPhone = (raw) => {
  const phone = (raw?.patient_phone || '').trim();
  if (!phone) return '';
  const code = (raw?.patient_phone_country_code || '').trim();
  return code && !phone.startsWith('+') && !phone.startsWith(code.replace('+', '')) ? `${code}${phone}` : phone;
};

/**
 * Latest patient location link for a booking, kept live.
 * The backend broadcasts `patient_location_updated` to the booking room when the patient shares; a slow poll
 * covers a missed socket event and shows Sent -> Opened progress while we wait.
 */
export const usePatientLocationShare = (bookingId, enabled = true) => {
  const [share, setShare] = useState(null);
  const activeIdRef = useRef(null);
  activeIdRef.current = enabled ? bookingId : null;

  const refresh = useCallback(async () => {
    if (!bookingId) return;
    try {
      const result = await patientLocationService.getBookingLocationShare(bookingId);
      if (activeIdRef.current === bookingId) setShare(result?.share || null);
    } catch (_) {
      // Transient - the next poll or socket event will catch up
    }
  }, [bookingId]);

  useEffect(() => {
    setShare(null);
    if (!enabled || !bookingId) return;
    refresh();
  }, [bookingId, enabled, refresh]);

  useEffect(() => {
    if (!enabled || !bookingId) return undefined;

    webSocketService.joinBookingRoom(bookingId);
    const handleUpdated = (data) => {
      if (data?.bookingId !== bookingId) return;
      if (data.patientLocation) {
        setShare((prev) => ({ ...(prev || {}), status: 'CAPTURED', location: data.patientLocation }));
      }
      refresh();
    };
    webSocketService.on('patient_location_updated', handleUpdated);
    return () => {
      webSocketService.off('patient_location_updated', handleUpdated);
      webSocketService.leaveBookingRoom(bookingId);
    };
  }, [bookingId, enabled, refresh]);

  const waiting = Boolean(share) && !TERMINAL_SHARE_STATUSES.includes(share.status);
  useEffect(() => {
    if (!enabled || !bookingId || !waiting) return undefined;
    const id = setInterval(refresh, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [enabled, bookingId, waiting, refresh]);

  return { share, patientLocation: toPatientLocation(share), refresh };
};

export default usePatientLocationShare;
